import React, { createContext, useContext, useState, useEffect } from "react";
import { useDashboardDomainContext } from "./DashboardDomainProvider";
import _ from "lodash";

export const DashboardSourceDataContext = createContext();

export const DashboardSourceDataContextProvider = ({ children }) => {
  const [sourceDataCache, setSourceDataCache] = useState({});
  const [cacheKeyLoadState, setCacheKeyLoadState] = useState({});
  const [visualErrors, setVisualErrors] = useState({});

  const { dashboardUserNavStateLib, dashboardLib, isViewMode } =
    useDashboardDomainContext();

  // Visuals now live under PascalCase details
  const visuals = dashboardLib?.activeDashboard?.[isViewMode ? "PublishedDetails" : "DraftDetails"]?.Visuals ?? [];

  const getSourceData = async (requests) => {

    //pull using the general manager Operaoitn we careated...
    //
    // TODO: replace with real call that can batch
    return [{ DataKey: "", Records: [], ErrorCodes: [] }];
  };

  useEffect(() => {
    if (visuals.length > 0) {
      const allPulls = getUniqueDataPulls(visuals);
      pullManyDatasets(allPulls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardLib?.activeDashboard, visuals, dashboardUserNavStateLib.selectedTab]);

  const clearSourceDataCache = () => {
    setSourceDataCache({});
  };

  const setCacheLoader = (cacheKey, isLoading) => {
    setCacheKeyLoadState((prev) => ({ ...prev, [cacheKey]: isLoading }));
  };

  const isVisualLoading = (visual) => {
    return cacheKeyLoadState[getCacheKey(visual)];
  };

  const getCacheKey = (visual) => {
    const dataFormKey = visual?.DashboardDatasetId ?? "";
    const formKeySuffix =
      (visual?.Filters ?? [])
        .slice()
        .sort((a, b) =>
          a.Column > b.Column ? 1 : a.Column < b.Column ? -1 :
          a.Operator > b.Operator ? 1 : a.Operator < b.Operator ? -1 :
          a.Value > b.Value ? 1 : a.Value < b.Value ? -1 : 0
        )
        .reduce((r, e) => (r + (e.Column ?? "") + (e.Operator ?? "") + (e.Value ?? "")).replace(" ", ""), "");

    return dataFormKey + formKeySuffix;
  };

  const getCachedDataFromVisual = (visual) => {
    return (visual ? sourceDataCache[getCacheKey(visual)] : []) ?? [];
  };

  const getUniqueDataPulls = (visuals) => {
    const completeDataForms = visuals?.reduce((form, visual) => {
      const cacheKey = getCacheKey(visual);
      if (!form[cacheKey]) {
        form[cacheKey] = {
          dataKey: visual?.DashboardDatasetId,
          cacheKey,
          //Expand this to more options... 
          filters: (visual?.Filters ?? []).map((filter) => {
            switch ((filter?.Operator || "").toLowerCase()) {
              case "contains":
                return {
                  Column: filter.Column,
                  Operator: "LIKE",
                  Value: `%${filter.Value}%`,
                };
              case "starts with":
                return {
                  Column: filter.Column,
                  Operator: "LIKE",
                  Value: `${filter.Value}%`,
                };
              case "ends with":
                return {
                  Column: filter.Column,
                  Operator: "LIKE",
                  Value: `%${filter.Value}`,
                };
              default:
                return filter;
            }
          }),
        };
      }

      return form;
    }, {});

    return completeDataForms;
  };

  // sourceDataRequest -> map of cacheKey -> request { dataKey, cacheKey, filters }
  const pullManyDatasets = async (sourceDataRequest) => {
    const splitRequests = Object.values(sourceDataRequest).filter(
      (request) => !Object.prototype.hasOwnProperty.call(sourceDataCache, request.cacheKey)
    );

    splitRequests.forEach(async (request) => {
      try {
        setCacheLoader(request.cacheKey, true);
        const [results] = await getSourceData([request]);

        if (results?.Records && (results?.ErrorCodes?.length ?? 0) === 0) {
          setSourceDataCache((prev) => ({ ...prev, [request.cacheKey]: results.Records }));
          setVisualErrors((prev) => {
            const updated = { ...prev };
            delete updated[request.cacheKey];
            return updated;
          });
        } else {
          setVisualErrors((prev) => ({
            ...prev,
            [request.cacheKey]: results?.ErrorCodes,
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setVisualErrors((prev) => ({
          ...prev,
          [request.cacheKey]: error,
        }));
      } finally {
        setCacheLoader(request.cacheKey, false);
      }
    });
  };

  const retryPullDataset = (visual) => {
    const request = getUniqueDataPulls([visual]);
    pullManyDatasets(request);
  };

  return (
    <DashboardSourceDataContext.Provider
      value={{
        getCacheKey,
        getCachedDataFromVisual,
        clearSourceDataCache,
        isVisualLoading,
        sourceDataCache,
        cacheKeyLoadState,
        visualErrors,
        retryPullDataset,
      }}
    >
      {children}
    </DashboardSourceDataContext.Provider>
  );
};

export const useDashboardSourceDataContext = () =>useContext(DashboardSourceDataContext);
